import { ListParcelParam, ParcelFilterObj } from './../domains/parcelDomain';
import { createModel } from '@rematch/core';
import type { RootModel } from 'models';
import { baseReducers, BaseState } from 'models/baseReducers';
import { Paged } from 'domains/appDomain';
import { Parcel, PartialParcel } from 'domains/parcelDomain';
import parcelService from 'services/parcelService';
import { FILTER_NOT_EXISTS } from 'utils/constants';

interface ParcelState extends BaseState {
  pagedData?: Paged<Parcel>;
  pageSize: number;
  searchValue: string;
  currentPage: number;
  curItem?: PartialParcel;
  noMarkupCount: number;
  baseSelectState: ParcelBaseSelectState;
  filterObj: ParcelFilterObj;
}

type ParcelBaseSelectState = {
  visible: boolean;
  curItem?: PartialParcel;
  pagedData?: Paged<Parcel>;
  currentPage: number;
  pageSize: number;
};

export const parcel = createModel<RootModel>()({
  state: {
    pagedData: undefined,
    searchValue: '',
    pageSize: 20,
    currentPage: 1,
    curItem: undefined,
    noMarkupCount: 0,
    baseSelectState: {
      visible: false,
      curItem: undefined,
      pagedData: undefined,
      currentPage: 1,
      pageSize: 10,
    },
    filterObj: {},
  } as ParcelState,
  reducers: {
    ...baseReducers<ParcelState>(),
    updateBaseSelectItem: (state: ParcelState, newItem: Parcel | undefined) => {
      state.baseSelectState.curItem = newItem;
      return state;
    },
    updateBaseSelectVisible: (state: ParcelState, visible: boolean) => {
      state.baseSelectState.visible = visible;
      return state;
    },
    reset: (state: ParcelState) => {
      state.pagedData = undefined;
      state.filterObj = {};
      state.baseSelectState = {
        visible: false,
        curItem: undefined,
        pagedData: undefined,
        currentPage: 1,
        pageSize: 10,
      };
      return state;
    },
  },
  effects: (dispatch) => {
    const { parcel } = dispatch;
    return {
      async getParcels(payload: ListParcelParam, state): Promise<void> {
        const pagedData = await parcelService.getPagedParcels(payload);
        parcel.setState({
          pagedData: pagedData,
          pageSize: payload.page.pageSize,
          currentPage: payload.page.currentPage,
        });
      },

      async getBaseParcels(payload: ListParcelParam, state): Promise<void> {
        const pagedData = await parcelService.getPagedBaseParcels(payload);
        parcel.setState({
          baseSelectState: {
            ...state.parcel.baseSelectState,
            pagedData,
            pageSize: payload.page.pageSize!,
            currentPage: payload.page.currentPage,
          },
        });
      },

      async saveParcel(payload: PartialParcel, state): Promise<void> {
        if (payload.id) {
          await parcelService.updateParcel(payload);
        } else {
          await parcelService.addParcel(payload);
        }

        await parcel.getParcels({
          page: {
            currentPage: state.parcel.currentPage,
            pageSize: state.parcel.pageSize,
          },
          query: {
            ...state.parcel.filterObj,
          },
        });
        await parcel.getNoMarkupCount();
      },

      async deleteParcel(payload: PartialParcel, state): Promise<void> {
        await parcelService.deleteParcel(payload);
        await parcel.getParcels({
          page: {
            currentPage: state.parcel.currentPage,
            pageSize: state.parcel.pageSize,
          },
          query: {
            ...state.parcel.filterObj,
          },
        });
        await parcel.getNoMarkupCount();
      },

      async getNoMarkupCount(): Promise<void> {
        const query: ListParcelParam = {
          page: {
            pageSize: 1,
            currentPage: 1,
          },
          query: {
            active: true,
            markupPct: FILTER_NOT_EXISTS,
          },
        };
        const resp = await parcelService.getPagedParcels(query);

        parcel.setState({
          noMarkupCount: resp.totalElements,
        });
      },

      async saveFilter(payload: ParcelFilterObj): Promise<void> {
        parcel.setState({
          currentPage: 1,
          filterObj: payload,
        });
      },

      async removeFilter(): Promise<void> {
        parcel.clearFilter();
      },
    };
  },
});
